import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, User, Users, Award, FileText, Search, ShieldCheck, Trophy, EyeOff, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { sortByName } from "@/lib/listSort";

const COACH_STATUS_STYLE = {
  active:    { bg: "#CCFF00", color: "#0A0A0A", label: "ACTIVE" },
  pending:   { bg: "#FF4500", color: "#0A0A0A", label: "PENDING" },
  suspended: { bg: "#525252", color: "#E5E5E5", label: "SUSPENDED" },
  rejected:  { bg: "#262626", color: "#A3A3A3", label: "REJECTED" },
};

function PlayerCard({ p, latest, onExclusionChanged }) {
  const ratings = latest?.ratings || {};
  const vals = Object.values(ratings).filter((v) => typeof v === "number");
  const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : "—";
  const level = p.assessment_level?.current;
  const excluded = !!p.team_share_excluded;
  const [busy, setBusy] = useState(false);
  const toggleShare = async (e) => {
    // Prevent the outer <Link> from navigating when the toggle is tapped.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const nextShared = excluded; // if currently excluded, we're turning share back ON
      await api.post(`/coach/players/${p.id}/team-share`, { shared: nextShared });
      toast.success(nextShared
        ? `${p.name} will now be included when you share with team coaches`
        : `${p.name} will be hidden from your team share`);
      onExclusionChanged?.(p.id, !nextShared);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Toggle failed");
    } finally { setBusy(false); }
  };
  return (
    <Link
      to={`/players/${p.id}${(p.assessment_status === "awaiting_feedback" || p.assessment_status === "scheduled") ? "#assessment" : ""}`}
      data-testid={`player-${p.id}`}
      className="card-surface p-5 hover:border-[#CCFF00] transition-colors group relative"
    >
      {/* Iter100 — Team-share exclusion toggle. Sits top-right so it never
          collides with the click surface of the outer Link (we prevent
          default on the button). */}
      <button
        type="button"
        onClick={toggleShare}
        disabled={busy}
        aria-label={excluded ? "Include in team share" : "Exclude from team share"}
        title={excluded ? "Currently hidden from team share. Tap to include." : "Currently shared with your team. Tap to exclude."}
        data-testid={`player-${p.id}-share-toggle`}
        className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center border transition-colors disabled:opacity-40"
        style={excluded
          ? { borderColor: "#FF4500", color: "#FF4500", background: "#0A0A0A" }
          : { borderColor: "#262626", color: "#525252", background: "#0A0A0A" }}
      >
        {excluded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-[#262626] flex items-center justify-center">
          <User className="w-5 h-5 text-[#A3A3A3]" />
        </div>
      </div>
      <div className="text-base font-semibold mb-1 truncate flex items-center gap-2">
        <span className="truncate">{p.name}</span>
        <ArrowRight className="w-3.5 h-3.5 text-[#A3A3A3] group-hover:text-[#CCFF00] group-hover:translate-x-1 transition-all shrink-0" />
      </div>
      <div className="text-xs text-[#A3A3A3] mb-3 truncate">{p.email}</div>
      {excluded && (
        <div
          className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-widest text-[#FF4500] border border-[#FF4500] px-1.5 py-0.5 mb-3"
          data-testid={`player-${p.id}-team-share-off`}
        >
          <EyeOff className="w-2.5 h-2.5" /> TEAM-SHARE OFF
        </div>
      )}
      <div className="flex items-center justify-between border-t border-[#262626] pt-4 gap-2">
        <div className="min-w-0">
          <div className="label-mono">{level ? "Level" : "Avg skill"}</div>
          <div className="h-display text-2xl text-[#CCFF00]">{level || avg}</div>
        </div>
        <div className="text-right min-w-0">
          <div className="label-mono">Assessment</div>
          <div className="text-[10px] font-semibold uppercase truncate" style={{
            color: p.assessment_status === "completed"
              ? "#CCFF00"
              : p.assessment_status === "awaiting_feedback"
                ? "#FFD700"       // amber — coach action needed
                : p.assessment_status === "scheduled"
                  ? "#66C2FF"     // blue — booked, not yet due
                  : "#FF4500"     // required — orange
          }}>
            {(p.assessment_status || "—").replace(/_/g, " ")}
          </div>
        </div>
      </div>
    </Link>
  );
}

function CoachCard({ c }) {
  const status = COACH_STATUS_STYLE[c.coach_status] || COACH_STATUS_STYLE.active;
  const sportLabel = (c.sport_focus || []).map((s) => s.toUpperCase()).join(" · ") || "ANY";
  const isSuper = (c.permissions || []).includes("super_admin");
  const isAdmin = isSuper || (c.permissions || []).includes("admin") || c.role === "admin";
  return (
    <Link to={`/admin/users/${c.id}`} data-testid={`coach-${c.id}`}
      className="card-surface p-5 hover:border-[#CCFF00] transition-colors group">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-[#262626] flex items-center justify-center">
          {isAdmin ? <ShieldCheck className="w-5 h-5 text-[#CCFF00]" /> : <Award className="w-5 h-5 text-[#FF4500]" />}
        </div>
        <span className="label-mono text-[9px] px-2 py-0.5" style={{ background: status.bg, color: status.color, fontWeight: 700 }}>
          {status.label}
        </span>
      </div>
      <div className="text-base font-semibold mb-1 truncate flex items-center gap-2">
        {c.name}
        {isSuper && <span className="label-mono text-[8px] px-1.5 py-0.5 bg-[#CCFF00] text-black">OWNER</span>}
        {!isSuper && isAdmin && <span className="label-mono text-[8px] px-1.5 py-0.5 bg-[#262626] text-[#CCFF00] border border-[#CCFF00]">ADMIN</span>}
      </div>
      <div className="text-xs text-[#A3A3A3] mb-4 truncate">{c.email}</div>
      <div className="text-[10px] text-[#525252] uppercase tracking-wider mb-3 truncate">{sportLabel}</div>
      <div className="grid grid-cols-2 gap-2 border-t border-[#262626] pt-4">
        <div>
          <div className="label-mono">Bookings</div>
          <div className="h-display text-2xl tracking-tighter text-[#CCFF00]" data-testid={`coach-${c.id}-bookings`}>{c.bookings_count}</div>
        </div>
        <div className="text-right">
          <div className="label-mono inline-flex items-center justify-end gap-1"><FileText className="w-2.5 h-2.5" /> Docs</div>
          <div className="h-display text-2xl tracking-tighter" style={{ color: c.documents_count > 0 ? "#CCFF00" : "#525252" }}>{c.documents_count}</div>
        </div>
      </div>
    </Link>
  );
}

export default function Players() {
  const { user } = useAuth();
  const perms = user?.permissions || [user?.role].filter(Boolean);
  const isAdmin = perms.includes("admin") || perms.includes("super_admin") || user?.role === "admin";

  const [tab, setTab] = useState("players"); // "players" | "coaches"
  const [players, setPlayers] = useState([]);
  const [latestMap, setLatestMap] = useState({});
  const [coaches, setCoaches] = useState([]);
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  const [query, setQuery] = useState("");
  // Coach-side filters — skill level, sex, and an age sort that never reveals the
  // actual age value (admins see full details elsewhere). All filters compose with
  // the search box.
  const [skillFilter, setSkillFilter] = useState("all");
  const [sexFilter, setSexFilter] = useState("all");
  const [ageSort, setAgeSort] = useState("none"); // "none" | "asc" | "desc"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/students");
        setPlayers(data);
        const map = {};
        await Promise.all(data.map(async (p) => {
          try {
            const r = await api.get(`/skills/${p.id}/latest`);
            map[p.id] = r.data;
          } catch { /* ignore — skill latest may 404 for new players */ }
        }));
        setLatestMap(map);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load players");
      }
    })();
  }, []);

  useEffect(() => {
    if (tab !== "coaches" || !isAdmin || coaches.length > 0) return;
    setLoadingCoaches(true);
    api.get("/coaches")
      .then((r) => setCoaches(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load coaches"))
      .finally(() => setLoadingCoaches(false));
  }, [tab, isAdmin, coaches.length]);

  const [nameSort, setNameSort] = useState("last");

  const filteredPlayers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? players.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q)
    ) : players;
    return sortByName(list, (p) => p.name, nameSort);
  }, [players, query, nameSort]);

  const filteredCoaches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? coaches.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.sport_focus || []).join(" ").toLowerCase().includes(q)
    ) : coaches;
    return sortByName(list, (c) => c.name, nameSort);
  }, [coaches, query, nameSort]);

  const tabs = [
    { id: "players", label: "Players", icon: Users, count: players.length, testid: "tab-players" },
    ...(isAdmin ? [{ id: "coaches", label: "Coaches", icon: Trophy, count: coaches.length || undefined, testid: "tab-coaches" }] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="label-mono mb-2">ROSTER</div>
        <h1 className="h-display text-4xl sm:text-5xl tracking-tighter" data-testid="roster-heading">
          {tab === "players" ? "Players" : "Coaches"}
          <span className="text-[#525252]"> ({tab === "players" ? filteredPlayers.length : filteredCoaches.length})</span>
        </h1>
      </div>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3 fade-up d1">
        <div className="inline-flex border border-[#262626]" role="tablist" data-testid="roster-tabs">
          {tabs.map(({ id, label, icon: Icon, count, testid }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(id)}
                data-testid={testid}
                className={`px-4 sm:px-5 py-2.5 text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-colors ${
                  active ? "bg-[#CCFF00] text-black font-bold" : "text-[#A3A3A3] hover:text-white hover:bg-[#141414]"
                }`}
              >
                <Icon strokeWidth={1.75} className="w-3.5 h-3.5" />
                {label}
                {typeof count === "number" && (
                  <span className={`text-[10px] ${active ? "text-black/60" : "text-[#525252]"}`}>({count})</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="inline-flex border border-[#262626]" role="radiogroup" aria-label="Sort by" data-testid="roster-sort">
            {[["last", "Last name"], ["first", "First name"]].map(([m, l]) => (
              <button key={m} type="button" role="radio" aria-checked={nameSort === m} onClick={() => setNameSort(m)} data-testid={`roster-sort-${m}`}
                className={`px-3 py-2.5 text-[10px] uppercase tracking-wider transition-colors ${nameSort === m ? "bg-[#CCFF00] text-black font-bold" : "text-[#A3A3A3] hover:text-white"}`}>
                {l} A–Z
              </button>
            ))}
          </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-3.5 h-3.5 absolute top-1/2 -translate-y-1/2 left-3 text-[#525252]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${tab}…`}
            data-testid="roster-search"
            className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none pl-9 pr-3 py-2.5 text-sm transition-colors"
          />
        </div>
        </div>
      </div>

      {tab === "players" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 fade-up d2" data-testid="players-grid">
          {filteredPlayers.map((p) => (
            <PlayerCard
              key={p.id}
              p={p}
              latest={latestMap[p.id]}
              onExclusionChanged={(id, excluded) => setPlayers((cur) => cur.map((x) => x.id === id ? { ...x, team_share_excluded: excluded } : x))}
            />
          ))}
          {!filteredPlayers.length && (
            <div className="text-sm text-[#A3A3A3] col-span-full">
              {query ? "No players match your search." : "No players yet."}
            </div>
          )}
        </div>
      )}

      {tab === "coaches" && isAdmin && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 fade-up d2" data-testid="coaches-grid">
          {loadingCoaches && !coaches.length ? (
            <div className="text-sm text-[#A3A3A3] col-span-full">Loading coaches…</div>
          ) : (
            <>
              {filteredCoaches.map((c) => (
                <CoachCard key={c.id} c={c} />
              ))}
              {!filteredCoaches.length && (
                <div className="text-sm text-[#A3A3A3] col-span-full">
                  {query ? "No coaches match your search." : "No coaches yet."}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
